<template>
    <div>
        <b-row v-if="shouldShowAll && !noConsultationPermissions">
            <b-col>
                <checkbox inline v-model="showAll">Show All</checkbox>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-alert :show="noConsultationPermissions" variant="danger" class="mt-2 mb-0">
                    <strong>
                        You do not have permissions to view consultations.
                    </strong>
                </b-alert>
                <b-table v-if="!noConsultationPermissions" :items="filteredConsultations" :fields="consultationFields" show-empty
                    empty-text="No records found" :tbody-tr-class="colorCodeTable" :per-page="pageSize"
                    :current-page="pageNumber">
                    <template #cell(patientName)="data">
                        <div>
                            <router-link v-if="patientNameAsLink"
                                :to="{ name: 'PatientDashboard', params: { id: data.item.patientId.toString() } }">
                                {{ data.item.patientName }}
                            </router-link>
                            <span v-else>
                                {{ data.item.patientName }}
                            </span>
                        </div>
                        <div>
                            <small class="font-weight-normal">
                                {{ data.item.dateOfBirth | formatLocalDate('L') }}
                            </small>
                        </div>
                    </template>
                    <template #cell(buttons)="data">
                        <div class="table-icon-actions">
                            <a v-if="$permissions.some(p => p.permissionCodeName === 'Pharmacists/Consultations/Cancel Consultation')"
                                href="#" v-b-tooltip.hover title="Cancel"
                                @click.prevent="detailsConsultation(data.item, true)">
                                <b-icon icon="x-circle-fill" class="mr-1"
                                    :variant="(data.item.status == 3 || data.item.status == 4) ? 'secondary' : 'danger'" />
                            </a>
                            <a href="#" v-b-tooltip.hover title="Details"
                                @click.prevent="detailsConsultation(data.item)">
                                <b-icon icon="pencil-square" variant="info" />
                            </a>
                        </div>
                    </template>
                    <template #cell(rxNumber)="{ value, item }">
                        <template v-if="value">
                            <router-link :to="{
                                name: 'PrescriptionDetails'
                                , params: {
                                    storeID: item.storeId,
                                    rxNumber: value,
                                    rfNumber: 0
                                }
                            }">
                                {{ `${item.storeId}-${value}` }}
                            </router-link>
                            <small class="text-muted font-weight-normal">
                                {{ item.programName }}
                            </small>
                            <div>
                                <small class="text-muted font-weight-normal">
                                    {{ item.drugName }}
                                </small>
                            </div>
                        </template>
                    </template>
                </b-table>

                <BottomPagerBar v-if="!noConsultationPermissions" v-model="pageNumber" :total-rows="filteredConsultations.length" :per-page="pageSize"
                    show-total item-name="consultation" show-page-size-selector @change-page-size="pageSize = $event">
                    <template #left>
                        <b-badge v-if="hidingCount > 0" variant="info" @click="showAll = true" style="cursor: pointer;"
                            class="mb-0">
                            Hiding {{ hidingCount }} {{ hidingCount === 1 ? 'Consultation' : 'Consultations' }}
                        </b-badge>
                    </template>
                </BottomPagerBar>
            </b-col>
        </b-row>
        <b-modal :id="modalId" title="Complete Consultation" :hide-footer='disableEditing' size="lg"
            @ok="updateConsultation" ok-variant="success" ok-title="Save Consultation" cancel-title="Close">
            <CompleteConsultationComponent ref="completeComponent" :consultation="consultation" :questions="questions"
                :answers="answers" :enable-edit="!disableEditing" :show-errors="showCompleteErrors" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Consultation, ConsultationCategory, ConsultationType } from '@/models/Consultation';
import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import CompleteConsultationComponent from '@/components/Consultation/CompleteConsultationComponent.vue';
import { Table, TableColumn } from 'element-ui';
import axios, { AxiosError } from 'axios';
import { Store } from "@/models/Store";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import moment from "moment";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { UserTaskAnswer, UserTaskQuestion } from '@/models/UserTask';
import { toRaw } from 'vue';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';

@Component({
    name: "ConsultationListComponent",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        CompleteConsultationComponent,
        Checkbox,
        BottomPagerBar,
    },
})
export default class ConsultationListComponent extends Vue {
    @Prop() private patientId!: number;
    @Prop() private store!: Store;
    @Prop() private enableCancel!: boolean;
    @Prop() private enableEdit!: boolean;
    @Prop({ default: false }) private patientNameAsLink!: boolean;
    @Prop() private hidePatient!: boolean;
    @Prop({ default: "" }) private searchTerm!: string;
    @Prop({ default: 'complete-consultation' }) private modalId!: string;

    @Ref("completeComponent") private completeComponent?: CompleteConsultationComponent;

    private consultations: Consultation[] = [];
    private consultation: Consultation = new Consultation();
    private currentConsultation: Consultation = new Consultation();

    private showAll: boolean = false;
    protected noConsultationPermissions = false;
    private pageSize = 50;
    private pageNumber = 1;

    get hasAnyClosedBy() {
        return this.filteredConsultations.some(c => c.closedBy);
    }

    get consultationFields() {
        return [
            { key: "storeName" },
            {
                key: "patientName",
                hidden: this.hidePatient,
            },
            {
                key: "requested",
                formatter: (value: any) => {
                    return moment.utc(value).local().format('L');
                },
            },
            {
                key: "createdBy",
                label: "Created By",
                formatter: (value: any) => {
                    if (!value) return "";
                    // If it's an email, show just the name part
                    if (value.includes("@")) {
                        return value.split("@")[0];
                    }
                    return value;
                },
            },
            { key: "statusName" },
            {
                key: "type",
                formatter: (value: any) => {
                    return ConsultationType[value];
                },
            },
            {
                key: "category",
                formatter: (value: any) => {
                    return ConsultationCategory[value];
                },
            },
            {
                key: "note",
            },
            {
                key: "lastActivity",
            },
            {
                key: "closedBy",
                label: "Closed By",
                hidden: !this.hasAnyClosedBy,
                formatter: (value: any) => {
                    if (!value) return "";
                    // If it's an email, show just the name part
                    if (value.includes("@")) {
                        return value.split("@")[0];
                    }
                    return value;
                },
            },
            {
                key: "rxNumber",
                label: "Script",
            },
            {
                key: "buttons",
                label: "",
            },
        ].filter(f => f.hidden !== true);
    }

    created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_Consultations', 50);
        this.getConsultation();
    }

    @Watch("pageSize")
    onPageSizePreferenceChanged() {
        savePageSizePreference(this, 'pageSize_Consultations', this.pageSize);
    }

    detailsConsultation(consultation: Consultation, cancel: boolean = false) {
        this.consultation = structuredClone(toRaw(consultation));
        this.currentConsultation = consultation;

        if (cancel) {
            if (consultation.status == 3) return;
            if (consultation.status == 4) return;
            this.consultation.status = 3;
            this.updateConsultation();
        } else {
            this.getQuestions();
            this.$bvModal.show(this.modalId);
        }
    }

    private showCompleteErrors = false;

    get disableEditing() {
        return !this.enableEdit || this.currentConsultation.status === 3 || this.currentConsultation.status === 4;
    }

    async updateConsultation() {
        this.showCompleteErrors = false;
        const canUpdate = this.enableEdit || this.enableCancel;
        if (!canUpdate) return;

        if (this.consultation.status === 4 && this.completeComponent?.getAnswersHaveErrors()) {
            this.showCompleteErrors = true;
            this.$notification(NotificationOptions.error("There are errors with your answers."));
            return;
        }

        try {
            const response = await axios.post<Consultation>('/Consultation/', {
                ...this.consultation,
                answers: this.completeComponent?.getCleanQuestionAnswers(),
            });

            if (response.data.id) {
                this.$notification(NotificationOptions.successSaveNotificationPreset("Consultation"));
                this.consultations.splice(
                    this.consultations.findIndex(c => c.id == this.consultation.id),
                    1,
                    new Consultation(response.data)
                );

                this.$bvModal.hide(this.modalId);

                this.$emit('consultationCompleted');
            }
        } catch (error) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Consultation", error as AxiosError));
        }
    }

    colorCodeTable(c: Consultation) {
        if (c?.status === 3) return "table-danger";
        if (c?.status === 3 || c?.status === 4) return "table-info";
        return;
    }

    /**
     * Attempts to parse a search term as a date in multiple formats.
     * Returns all valid interpretations of the date to handle US vs UK/EU ambiguity.
     */
    private tryParseSearchDates(searchTerm: string): moment.Moment[] {
        // US formats (month/day/year) - checked first
        const usFormats = [
            'M/D/YYYY',   // 1/8/2009
            'M/D/YY',     // 1/8/09
            'MM/DD/YYYY', // 01/08/2009
            'MM/DD/YY',   // 01/08/09
        ];

        // UK/EU formats (day/month/year)
        const euFormats = [
            'D/M/YYYY',   // 8/1/2009 (8th January)
            'D/M/YY',     // 8/1/09
            'DD/MM/YYYY', // 08/01/2009
            'DD/MM/YY',   // 08/01/09
        ];

        const results: moment.Moment[] = [];

        // Try US formats first (preferred for ambiguous dates)
        for (const format of usFormats) {
            const parsed = moment(searchTerm, format, true);
            if (parsed.isValid()) {
                results.push(parsed);
                break; // Only need one US interpretation
            }
        }

        // Try EU formats (may produce different date for ambiguous inputs)
        for (const format of euFormats) {
            const parsed = moment(searchTerm, format, true);
            if (parsed.isValid()) {
                // Only add if it's a different date than US interpretation
                const euDate = parsed.format('YYYY-MM-DD');
                if (!results.some(r => r.format('YYYY-MM-DD') === euDate)) {
                    results.push(parsed);
                }
                break;
            }
        }

        return results;
    }

    @Watch('patientId')
    @Watch('store')
    @Watch('showAll')
    resetPage() {
        this.pageNumber = 1;
    }

    get filteredConsultations() {
        let consults = this.consultations;
        if (this.searchTerm.length > 0) {
            consults = consults.filter(c => {
                const nameMatch = c.patientName?.toLowerCase().includes(this.searchTerm.toLowerCase());
                if (nameMatch) return true;

                // DoB matching with multiple date format support
                let dobMatch = false;
                if (c.dateOfBirth) {
                    const searchDates = this.tryParseSearchDates(this.searchTerm);
                    if (searchDates.length > 0) {
                        // Compare dates - match if ANY valid interpretation matches
                        const consultationDob = moment(c.dateOfBirth).format('YYYY-MM-DD');
                        dobMatch = searchDates.some(sd => sd.format('YYYY-MM-DD') === consultationDob);
                    } else {
                        // Fall back to substring matching for partial input
                        dobMatch = new Date(c.dateOfBirth).toLocaleDateString().includes(this.searchTerm);
                    }
                }
                if (dobMatch) return true;
                const drugNameMatch = c.drugName?.toLowerCase().includes(this.searchTerm.toLowerCase());
                if (drugNameMatch) return true;
                const typeMatch = ConsultationType[c.type]?.toLowerCase().includes(this.searchTerm.toLowerCase());
                if (typeMatch) return true;
                const categoryMatch = ConsultationCategory[c.category]?.toString().toLowerCase().includes(this.searchTerm.toLowerCase());
                if (categoryMatch) return true;
                const programMatch = c.programName?.toLowerCase().includes(this.searchTerm.toLowerCase());
                if (programMatch) return true;
                const createdByMatch = c.createdBy?.toLowerCase().includes(this.searchTerm.toLowerCase());
                if (createdByMatch) return true;
                const closedByMatch = c.closedBy?.toLowerCase().includes(this.searchTerm.toLowerCase());
                return !!closedByMatch;
            });
        }
        if (this.showAll) return consults;
        return consults?.filter(c => ![3, 4].includes(c.status));
    }

    get hidingCount() {
        return (this.consultations?.length ?? 0) - (this.filteredConsultations?.length ?? 0);
    }

    get shouldShowAll() {
        return !!this.consultations?.find(c => [3, 4].includes(c.status));
    }

    @Watch('patientId')
    @Watch('store')
    getConsultation() {
        let request = '/Consultation/';
        if (this.patientId != 0) {
            request += this.patientId;
        }

        if (this.store && this.store.id) {
            request += '/' + this.store.id;
        }

        const config = {
            params: {
                includeAllStatuses: this.patientId > 0,
            },
        };

        axios.get<Consultation[]>(request, config)
             .then(response => {
                 this.consultations = response.data.map(c => new Consultation(c));
                 this.noConsultationPermissions = false;
             })
             .catch(error => {
                 const err = error as AxiosError;
                 this.noConsultationPermissions = err.status === 403;
                 if (!this.noConsultationPermissions) {
                     this.$notification(NotificationOptions.error(error));
                 }
             });
    }

    protected questions: UserTaskQuestion[] = [];
    protected answers: UserTaskAnswer[] = [];

    async getQuestions() {
        if (!this.consultation.completionQuestionSetId) {
            this.questions = [];
            this.answers = [];
            return;
        }
        try {
            const resp = await this.$http.get(`/consultation/questions/${this.consultation.id}`);
            this.questions = resp.data.questions;
            this.answers = resp.data.answers;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }
}
</script>

<style scoped></style>
